import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar, Clock, Building2, Phone, Video, ChevronRight, Loader2, CheckCircle2, FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { format } from 'date-fns';
import { toManilaDate, isManilaToday, isManilaTomorrow } from '../lib/dateUtils';
import InterviewCard from './InterviewCard';

const UpcomingInterviews = ({ isEmployer = false, limit = 5 }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [completedInterviews, setCompletedInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const autoOpenHandled = useRef(false);

  const fetchUpcomingInterviews = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/interviews', {
        params: { upcoming: true, limit },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        setUpcomingInterviews(response.data.interviews);
      }
    } catch (error) {
      console.error('Error fetching upcoming interviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedInterviews = async () => {
    setLoadingCompleted(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/interviews', {
        params: { status: 'completed', limit: 20 },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        setCompletedInterviews(response.data.interviews);
      }
    } catch (error) {
      console.error('Error fetching completed interviews:', error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  useEffect(() => {
    fetchUpcomingInterviews();
  }, []);

  // Auto-open interview modal when navigated from notification
  useEffect(() => {
    if (autoOpenHandled.current) return;
    if (searchParams.get('showInterview') === 'true' && upcomingInterviews.length > 0) {
      autoOpenHandled.current = true;
      // Select the first pending interview, or the first one if none pending
      const pending = upcomingInterviews.find(iv => iv.status === 'pending' || iv.status === 'rescheduled');
      setSelectedInterview(pending || upcomingInterviews[0]);
      // Remove param so refresh doesn't re-trigger
      setSearchParams((prev) => { prev.delete('showInterview'); return prev; }, { replace: true });
    }
  }, [upcomingInterviews]);

  // Fetch completed interviews when tab changes
  useEffect(() => {
    if (activeTab === 'completed' && completedInterviews.length === 0) {
      fetchCompletedInterviews();
    }
  }, [activeTab]);

  const handleInterviewUpdate = () => {
    fetchUpcomingInterviews();
    if (activeTab === 'completed') {
      fetchCompletedInterviews();
    }
    setSelectedInterview(null);
  };

  const interviewTypeIcons = {
    in_person: <Building2 size={14} className="text-blue-600" />,
    phone: <Phone size={14} className="text-green-600" />,
    video: <Video size={14} className="text-purple-600" />
  };

  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    rescheduled: 'bg-orange-100 text-orange-800 border-orange-300',
    completed: 'bg-blue-100 text-blue-800'
  };

  const statusLabels = {
    pending: t('interview.pending'),
    confirmed: t('interview.confirmed'),
    rescheduled: 'Reschedule Requested',
    completed: t('interview.completed')
  };

  const renderInterviewItem = (interview, isCompleted = false) => {
    const scheduledDate = toManilaDate(interview.scheduled_at);
    const isUrgent = !isCompleted && (isManilaToday(scheduledDate) || isManilaTomorrow(scheduledDate));
    const needsAction = interview.status === 'rescheduled';

    return (
      <div
        key={interview.id}
        className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors hover:bg-muted/50 cursor-pointer ${
          needsAction ? 'border-orange-400 bg-orange-50' :
          isUrgent ? 'border-primary/30 bg-primary/5' :
          isCompleted ? 'border-blue-200 bg-blue-50/30' : ''
        }`}
        onClick={() => setSelectedInterview(interview)}
      >
        <div className="flex items-center gap-3">
          {/* Date Badge */}
          <div className={`text-center px-3 py-2 rounded-lg min-w-[60px] ${
            isCompleted
              ? 'bg-blue-500 text-white'
              : needsAction
                ? 'bg-orange-500 text-white'
                : isManilaToday(scheduledDate)
                  ? 'bg-red-500 text-white'
                  : isManilaTomorrow(scheduledDate)
                    ? 'bg-orange-500 text-white'
                    : 'bg-primary text-white'
          }`}>
            <div className="text-lg font-bold">{format(scheduledDate, 'd')}</div>
            <div className="text-xs uppercase">{format(scheduledDate, 'MMM')}</div>
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium truncate">
                {isEmployer
                  ? interview.applicant?.name
                  : interview.employer?.business_name || interview.employer?.name
                }
              </p>
              <Badge className={statusStyles[interview.status]} variant="outline">
                {statusLabels[interview.status] || interview.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {interview.job?.title}
            </p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {format(scheduledDate, 'h:mm a')}
              </span>
              <span className="flex items-center gap-1">
                {interviewTypeIcons[interview.interview_type]}
                {interview.interview_type === 'in_person' ? t('interview.inPerson') :
                 interview.interview_type === 'phone' ? t('interview.phone') : t('interview.video')}
              </span>
              {/* Show notes indicator for completed interviews */}
              {isCompleted && interview.employer_notes && (
                <span className="flex items-center gap-1 text-blue-600">
                  <FileText size={12} />
                  Has notes
                </span>
              )}
            </div>
          </div>

          <ChevronRight size={20} className="text-muted-foreground flex-shrink-0" />
        </div>

        {/* Reschedule Message */}
        {needsAction && interview.applicant_response && (
          <div className="ml-[72px] text-sm text-orange-700 italic bg-orange-100 p-2 rounded">
            "{interview.applicant_response}"
          </div>
        )}

        {/* Show notes preview for completed interviews */}
        {isCompleted && interview.employer_notes && (
          <div className="ml-[72px] text-sm text-blue-700 bg-blue-100 p-2 rounded">
            <span className="font-medium">Notes: </span>
            {interview.employer_notes.length > 100
              ? `${interview.employer_notes.substring(0, 100)}...`
              : interview.employer_notes
            }
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            {t('interview.upcomingInterviews')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasAnyInterviews = upcomingInterviews.length > 0 || completedInterviews.length > 0;

  // Show tabs only for employers (they have notes to review)
  if (isEmployer) {
    return (
      <>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar size={20} className="text-primary" />
              Interviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="upcoming" className="flex items-center gap-2">
                  <Calendar size={14} />
                  Upcoming
                  {upcomingInterviews.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {upcomingInterviews.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="completed" className="flex items-center gap-2">
                  <CheckCircle2 size={14} />
                  Completed
                  {completedInterviews.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {completedInterviews.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming" className="space-y-3 mt-0">
                {upcomingInterviews.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Calendar size={40} className="mx-auto mb-2 opacity-50" />
                    <p>{t('interview.noUpcomingInterviews')}</p>
                    <p className="text-sm mt-1">{t('interview.scheduleWithApplicants')}</p>
                  </div>
                ) : (
                  <>
                    {upcomingInterviews.map((interview) => renderInterviewItem(interview, false))}
                    {upcomingInterviews.length >= limit && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate('/dashboard')}
                      >
                        {t('interview.viewAllInterviews')}
                        <ChevronRight size={16} className="ml-1" />
                      </Button>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="completed" className="space-y-3 mt-0">
                {loadingCompleted ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-muted-foreground" />
                  </div>
                ) : completedInterviews.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <CheckCircle2 size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No completed interviews yet</p>
                    <p className="text-sm mt-1">Completed interviews will appear here with your notes</p>
                  </div>
                ) : (
                  completedInterviews.map((interview) => renderInterviewItem(interview, true))
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Interview Detail Modal */}
        <Dialog open={!!selectedInterview} onOpenChange={(open) => !open && setSelectedInterview(null)}>
          <DialogContent className="max-w-lg p-0 overflow-y-auto max-h-[85vh]">
            <DialogHeader className="sr-only">
              <DialogTitle>Interview Details</DialogTitle>
              <DialogDescription>View and manage interview details</DialogDescription>
            </DialogHeader>
            {selectedInterview && (
              <InterviewCard
                interview={selectedInterview}
                isEmployer={isEmployer}
                onUpdate={handleInterviewUpdate}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Simple view for job seekers (no tabs needed)
  if (upcomingInterviews.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            {t('interview.upcomingInterviews')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <Calendar size={40} className="mx-auto mb-2 opacity-50" />
            <p>{t('interview.noUpcomingInterviews')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            {t('interview.upcomingInterviews')}
            {upcomingInterviews.length > 0 && (
              <Badge variant="secondary" className="ml-auto">
                {upcomingInterviews.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {upcomingInterviews.map((interview) => renderInterviewItem(interview, false))}

          {upcomingInterviews.length >= limit && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              {t('interview.viewAllInterviews')}
              <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Interview Detail Modal */}
      <Dialog open={!!selectedInterview} onOpenChange={(open) => !open && setSelectedInterview(null)}>
        <DialogContent className="max-w-lg p-0 overflow-y-auto max-h-[85vh]">
          <DialogHeader className="sr-only">
            <DialogTitle>Interview Details</DialogTitle>
            <DialogDescription>View and manage interview details</DialogDescription>
          </DialogHeader>
          {selectedInterview && (
            <InterviewCard
              interview={selectedInterview}
              isEmployer={isEmployer}
              onUpdate={handleInterviewUpdate}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpcomingInterviews;
